import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DEFAULTVALUE from '@/app/constants/defaultValue';
import { DetailsBooksItems } from '@/app/models/detailsBooks';
import { useUpdateDetailsBookMutation } from '@/app/services/detailsBooksApi';
import { formatDateForBackend } from '@/app/utils/dateUtils';
import formatterMonetaryValue, { formatCurrency } from '@/app/utils/numberUtils';
import FormFieldDate from '@/components/common/DateField';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import useFormattedCurrency from '@/hooks/useFormattedCurrency';
import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import FormField from '../baseForms/FormField';
import FormFieldLong from '../baseForms/FormFieldLong';
import FormFieldQuantity from '../baseForms/FormFieldQuantity';
import LaunchDetailsBox from '../baseForms/info/LaunchDetailsBox';

interface NewEditBookDetailsProps {
  selectItem: DetailsBooksItems;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditBookDetails(props: NewEditBookDetailsProps) {
  const { t } = useTranslation(['books', 'common']);
  const theme = useTheme();
  const { selectItem, setDrawerOpen } = props;
  const [launchComplement] = useState<string>(selectItem?.complement ? selectItem.complement : '');

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { value: totalValue, setValue: setTotalValue } = useFormattedCurrency(selectItem?.value);

  const [description, setDescription] = useState<string>(
    selectItem?.description != null ? selectItem?.description : '',
  );

  const [quantity, setQuantity] = useState<number | null>(selectItem?.quantity || DEFAULTVALUE);

  const [justification, setJustification] = useState<string>(selectItem?.justify != null ? selectItem?.justify : '');
  const [expenseDate, setExpenseDate] = useState<string>(
    selectItem?.expenseDate ? dayjs(selectItem?.expenseDate).format('DD/MM/YYYY') : '',
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const [update] = useUpdateDetailsBookMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        id: selectItem.id,
        description: description as string,
        quantity: quantity as number,
        value: parseFloat(totalValue.replace('R$', '').replace('.', '').replace(',', '.')) || DEFAULTVALUE,
        justify: justification,
        expenseDate: formatDateForBackend(expenseDate as string),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('success.transferred') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const hasNegative = val.includes('-');
    const cleaned = val.replace(/[^0-9]/g, '');
    const raw = hasNegative ? `-${cleaned}` : cleaned;
    setTotalValue(formatCurrency(raw));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('common:edit.detail')} />

        <LaunchDetailsBox
          totalValue={formatterMonetaryValue(selectItem?.value || null)}
          launchComplement={launchComplement}
          complementDisabled
        />

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        />
        <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
          <FormField label={t('title') as string} value={description} onChange={handleChange} />
          <FormField label={t('common:total.value') as string} value={totalValue} onChange={handleValueChange} />
          <FormFieldQuantity label={t('common:quantity')} value={quantity} onChange={setQuantity} />
          <FormFieldDate label={t('common:expenseDate')} value={expenseDate as string} onChange={setExpenseDate} />
          <FormFieldLong
            label={t('common:justification')}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Box>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={!expenseDate} />
      </Box>
      S
    </Box>
  );
}
